import { File01 } from "@untitledui/icons";
import type {
	ComponentPropsWithRef,
	FC,
	ReactNode,
	RefAttributes,
} from "react";
import { useEffect, useRef, useState } from "react";
import type {
	DialogProps as AriaDialogProps,
	ModalOverlayProps as AriaModalOverlayProps,
	ModalRenderProps as AriaModalRenderProps,
} from "react-aria-components";
import {
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
} from "react-aria-components";
import { ModalOverlay as DesignSystemOverlay } from "@/components/application/modals/modal";
import type { IconComponentType } from "@/components/base/badges/badge-types";
import { BadgeWithIcon } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { ScrollBody } from "./ScrollBody";

// Overlay behavior configuration types
type OverlayBehavior =
	| "dismissable" // Default: close on any outside click
	| "persistent" // Never close on outside click
	| "interactive" // Smart: close only on non-interactive clicks
	| "custom"; // User-defined shouldCloseOnInteractOutside logic

// Backdrop styling configuration types
type BackdropColor =
	| "overlay" // Default theme overlay color
	| "black" // Pure black backdrop
	| "white" // Pure white backdrop
	| "brand" // Brand color backdrop
	| "gray" // Gray backdrop
	| "transparent"; // No backdrop color

type BackdropOpacity =
	| "0"
	| "5"
	| "10"
	| "20"
	| "25"
	| "30"
	| "40"
	| "50"
	| "60"
	| "70"
	| "75"
	| "80"
	| "90"
	| "95"
	| "100";

type BackdropBlur = "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

type BackdropPreset =
	| "default" // Figma Overlay — bg-alpha-black-20 (Application/Modals/ModalOverlay)
	| "light" // bg-white/80 backdrop-blur-sm
	| "dark" // bg-black/80 backdrop-blur-sm
	| "glass" // bg-white/20 backdrop-blur-md
	| "frosted" // bg-overlay/60 backdrop-blur-lg
	| "minimal" // bg-overlay/40
	| "strong" // bg-overlay/90
	| "transparent"; // bg-transparent

interface BackdropConfig {
	color?: BackdropColor;
	opacity?: BackdropOpacity;
	blur?: BackdropBlur;
	preset?: BackdropPreset;
	className?: string; // Custom override
}

// Interactive element detection utility
const isInteractiveElement = (
	element: Element,
	drawerRef?: React.RefObject<HTMLDivElement | null>,
): boolean => {
	// Debug logging to understand what's being clicked
	// console.log("🔍 Drawer click detection:", {
	// 	tagName: element.tagName,
	// 	className: element.className,
	// 	role: element.getAttribute("role"),
	// 	dataRac: element.getAttribute("data-rac"),
	// 	dataTrigger: element.getAttribute("data-trigger"),
	// 	dataKey: element.getAttribute("data-key"),
	// 	ariaExpanded: element.getAttribute("aria-expanded"),
	// 	allDataAttributes: Array.from(element.attributes)
	// 		.filter((attr) => attr.name.startsWith("data-"))
	// 		.map((attr) => `${attr.name}="${attr.value}"`),
	// 	parentChain: (() => {
	// 		const parents = [];
	// 		let current = element.parentElement;
	// 		let depth = 0;
	// 		while (current && depth < 3) {
	// 			parents.push({
	// 				tagName: current.tagName,
	// 				className: current.className,
	// 				role: current.getAttribute("role"),
	// 				dataRac: current.getAttribute("data-rac"),
	// 				dataTrigger: current.getAttribute("data-trigger"),
	// 			});
	// 			current = current.parentElement;
	// 			depth++;
	// 		}
	// 		return parents;
	// 	})(),
	// });

	// IMMEDIATE CHECK: React Aria Components universal attribute
	if (element.hasAttribute("data-rac") || element.closest("[data-rac]")) {
		// console.log("✅ Found React Aria Component - preventing drawer close");
		return true;
	}

	// SPECIAL CASE: Handle clicks on document.body that might be from React Aria overlays
	// This happens when React Aria overlays are rendered outside the normal DOM tree
	// and the click event bubbles up to the body element
	if (element === document.body) {
		// console.log("🎯 Body element clicked - checking for React Aria overlays");

		// Check if there are any Select components inside the drawer that might be open
		if (drawerRef?.current) {
			// Look for any elements with aria-expanded="true" (open Select dropdowns)
			const openSelects = drawerRef.current.querySelectorAll(
				'[aria-expanded="true"]',
			);

			if (openSelects.length > 0) {
				// console.log(
				// 	"✅ Found open Select components in drawer - preventing drawer close",
				// );
				return true;
			}

			// Also check for any React Aria components that might be in an open state
			const reactAriaComponents =
				drawerRef.current.querySelectorAll("[data-rac]");

			if (reactAriaComponents.length > 0) {
				// console.log(
				// 	"✅ Found React Aria components in drawer - preventing drawer close",
				// );
				return true;
			}
		}

		// Check for React Aria overlays that are direct children of body
		const bodyChildren = Array.from(document.body.children);
		const hasReactAriaOverlay = bodyChildren.some((child) => {
			if (child instanceof HTMLElement) {
				// Check for React Aria attributes
				if (
					child.hasAttribute("data-rac") ||
					child.hasAttribute("data-react-aria-top-layer") ||
					child.hasAttribute("data-overlay-container") ||
					child.hasAttribute("data-popover")
				) {
					return true;
				}

				// Check for high z-index positioned elements
				const style = window.getComputedStyle(child);
				const zIndex = parseInt(style.zIndex || "0", 10);
				if (
					zIndex > 1000 &&
					(style.position === "fixed" || style.position === "absolute")
				) {
					return true;
				}

				// Check for ARIA roles that indicate overlays
				const role = child.getAttribute("role");
				if (role === "listbox" || role === "menu" || role === "dialog") {
					return true;
				}
			}
			return false;
		});

		if (hasReactAriaOverlay) {
			// console.log(
			// 	"✅ Found React Aria overlay as body child - preventing drawer close",
			// );
			return true;
		}
	}

	// Check for React Aria overlays (Popover, Tooltip, etc.) - these should never close the drawer
	const ariaOverlaySelectors = [
		// React Aria Components - this is the key attribute!
		"[data-rac]",
		// React Aria specific triggers
		'[data-trigger="Select"]',
		'[data-trigger="ComboBox"]',
		'[data-trigger="Menu"]',
		// ARIA roles for overlays
		'[role="dialog"]',
		'[role="listbox"]',
		'[role="menu"]',
		'[role="tooltip"]',
		'[role="combobox"]',
		'[role="option"]',
		'[role="menuitem"]',
		'[role="presentation"]',
		// React Aria collection and selection
		"[data-collection]",
		"[data-key]",
		"[data-selection-mode]",
		"[data-react-aria-pressable]",
		// React Aria state attributes
		'[aria-expanded="true"]',
		"[data-pressed]",
		"[data-selected]",
		"[data-focused]",
		"[data-focus-visible]",
		"[data-hovered]",
		// React Aria focus management
		"[data-focus-scope-start]",
		"[data-focus-scope-end]",
		// React Aria layout attributes
		"[data-layout]",
		"[data-orientation]",
		"[data-placement]",
		// Legacy React Aria patterns
		'[class*="react-aria"]',
		"[data-react-aria]",
		"[data-react-aria-top-layer]",
		"[data-overlay-container]",
		"[data-overlay]",
		// Popover and overlay containers
		"[data-popover]",
		".popover",
	];

	for (const selector of ariaOverlaySelectors) {
		if (element.matches?.(selector) || element.closest(selector)) {
			return true;
		}
	}

	// Special handling for React Aria Select dropdowns and overlays
	// Check if element is part of a Select dropdown by looking for specific patterns
	const isSelectDropdown = (() => {
		// Check if element has listbox role or is inside one
		if (
			element.getAttribute("role") === "listbox" ||
			element.closest('[role="listbox"]')
		) {
			return true;
		}

		// Check if element has option role or is inside one
		if (
			element.getAttribute("role") === "option" ||
			element.closest('[role="option"]')
		) {
			return true;
		}

		// Check if element is inside a popover (React Aria Popover component)
		let current: Element | null = element;
		while (current && current !== document.body) {
			// Check for common React Aria Popover patterns
			if (current instanceof HTMLElement && current.tagName === "DIV") {
				const style = current.style;
				if (
					(style.position === "fixed" || style.position === "absolute") &&
					style.zIndex &&
					parseInt(style.zIndex, 10) > 1000
				) {
					// This looks like a React Aria Popover
					return true;
				}
			}

			// Check for React Aria specific attributes on parents
			if (
				current.hasAttribute("data-react-aria-top-layer") ||
				current.hasAttribute("data-overlay-container") ||
				current.getAttribute("role") === "presentation"
			) {
				return true;
			}

			current = current.parentElement;
		}

		return false;
	})();

	if (isSelectDropdown) {
		return true;
	}

	// Check if element is a direct child of body (common for React Aria overlays)
	const isBodyLevelOverlay = (() => {
		let current: Element | null = element;
		while (current && current !== document.body) {
			// If we find a parent that's a direct child of body and looks like an overlay
			if (current.parentElement === document.body) {
				// Check if this looks like an overlay element
				if (current instanceof HTMLElement) {
					const style = current.style;
					const hasOverlayStyles =
						style.position === "fixed" ||
						style.position === "absolute" ||
						parseInt(style.zIndex || "0", 10) > 100;

					const hasOverlayAttributes =
						current.hasAttribute("role") ||
						current.hasAttribute("aria-hidden") ||
						current.className.includes("popover") ||
						current.className.includes("overlay") ||
						current.className.includes("dropdown");

					// Check for React Aria Components specific attributes
					const hasReactAriaAttributes =
						current.hasAttribute("data-rac") ||
						current.hasAttribute("data-react-aria-top-layer") ||
						current.hasAttribute("data-overlay-container") ||
						current.hasAttribute("data-popover");

					if (
						hasOverlayStyles ||
						hasOverlayAttributes ||
						hasReactAriaAttributes
					) {
						return true;
					}
				}
			}
			current = current.parentElement;
		}
		return false;
	})();

	if (isBodyLevelOverlay) {
		return true;
	}

	// Additional check for React Aria Components rendered outside drawer
	// This handles cases where React Aria Components create overlays that are
	// rendered as direct children of body but might not be caught by the above checks
	const isReactAriaOverlay = (() => {
		// Check if the element or any of its parents have React Aria attributes
		let current: Element | null = element;
		while (current && current !== document.body) {
			// Check for React Aria Components universal attribute
			if (current.hasAttribute("data-rac")) {
				return true;
			}

			// Check for React Aria overlay containers
			if (
				current.hasAttribute("data-react-aria-top-layer") ||
				current.hasAttribute("data-overlay-container") ||
				current.hasAttribute("data-popover")
			) {
				return true;
			}

			// Check for high z-index elements that are likely overlays
			if (current instanceof HTMLElement) {
				const computedStyle = window.getComputedStyle(current);
				const zIndex = parseInt(computedStyle.zIndex || "0", 10);

				// High z-index elements are likely overlays
				if (zIndex > 1000) {
					return true;
				}
			}

			current = current.parentElement;
		}
		return false;
	})();

	if (isReactAriaOverlay) {
		return true;
	}

	// Check if the clicked element is connected to a React Aria Component inside the drawer
	// This handles cases where the overlay is rendered outside but is logically connected
	// to a component inside the drawer (like a Select trigger)
	const isConnectedToDrawerComponent = (() => {
		// Look for elements that might be connected to drawer components through React Aria
		// Check for common React Aria connection patterns
		const ariaAttributes = [
			"aria-controls",
			"aria-describedby",
			"aria-labelledby",
			"aria-owns",
			"data-trigger",
			"data-key",
		];

		for (const attr of ariaAttributes) {
			const value = element.getAttribute(attr);
			if (value) {
				// Check if the referenced element is inside the drawer
				const referencedElement = document.getElementById(value);
				if (
					referencedElement &&
					drawerRef?.current?.contains(referencedElement)
				) {
					return true;
				}
			}
		}

		// Check if this element is part of a React Aria collection that's connected to the drawer
		const collectionElement = element.closest("[data-collection]");
		if (collectionElement) {
			// Check if the collection is inside the drawer
			if (drawerRef?.current?.contains(collectionElement)) {
				return true;
			}
		}

		return false;
	})();

	if (isConnectedToDrawerComponent) {
		return true;
	}

	// Check for TanStack DevTools - these should never close the drawer
	const devToolsSelectors = [
		"[data-tanstack-devtools]",
		"[data-tanstack-devtools-panel]",
		".tanstack-devtools",
		".tanstack-devtools-panel",
		"#tanstack-devtools",
		"#tanstack-devtools-panel",
		// React Query DevTools
		"[data-react-query-devtools]",
		".react-query-devtools",
		// React Router DevTools
		"[data-react-router-devtools]",
		".react-router-devtools",
		// React Form DevTools
		"[data-react-form-devtools]",
		".react-form-devtools",
		// Generic DevTools patterns
		'[class*="devtools"]',
		'[id*="devtools"]',
		'[data-testid*="devtools"]',
	];

	for (const selector of devToolsSelectors) {
		if (element.matches?.(selector) || element.closest(selector)) {
			return true;
		}
	}

	// Check interactive HTML tags
	const interactiveTags = [
		"BUTTON",
		"A",
		"INPUT",
		"SELECT",
		"TEXTAREA",
		"DETAILS",
		"SUMMARY",
	];
	if (interactiveTags.includes(element.tagName)) return true;

	// Check ARIA roles that indicate interactivity
	const interactiveRoles = [
		"button",
		"link",
		"textbox",
		"combobox",
		"listbox",
		"option",
		"menuitem",
		"tab",
		"switch",
		"checkbox",
		"radio",
		"slider",
		"spinbutton",
		"searchbox",
		"grid",
		"gridcell",
		"row",
		"tree",
		"treeitem",
	];
	const role = element.getAttribute("role");
	if (role && interactiveRoles.includes(role)) return true;

	// Check for interactive attributes
	if (
		element.hasAttribute("onclick") ||
		element.hasAttribute("onkeydown") ||
		element.hasAttribute("tabindex") ||
		element.hasAttribute("data-clickable") ||
		element.hasAttribute("data-interactive")
	)
		return true;

	// Check for contenteditable
	if (
		element.hasAttribute("contenteditable") &&
		element.getAttribute("contenteditable") !== "false"
	)
		return true;

	// Check if element is focusable (has tabindex >= 0)
	const tabIndex = element.getAttribute("tabindex");
	if (tabIndex && parseInt(tabIndex, 10) >= 0) return true;

	// Check for closest interactive parent (traverse up the DOM)
	const interactiveSelector = [
		"button",
		"a[href]",
		"input",
		"select",
		"textarea",
		"details",
		"summary",
		'[role="button"]',
		'[role="link"]',
		'[role="textbox"]',
		'[role="combobox"]',
		'[role="listbox"]',
		'[role="option"]',
		'[role="menuitem"]',
		'[role="tab"]',
		'[role="row"]',
		'[role="switch"]',
		'[role="checkbox"]',
		'[role="radio"]',
		'[role="slider"]',
		'[role="dialog"]',
		'[role="menu"]',
		'[role="tooltip"]',
		"[onclick]",
		"[onkeydown]",
		"[tabindex]",
		"[data-clickable]",
		"[data-interactive]",
		'[contenteditable="true"]',
		'[contenteditable=""]',
		'[aria-expanded="true"]',
		// React Aria Components - the universal selector!
		"[data-rac]",
		"[data-key]",
		"[data-collection]",
		"[data-selection-mode]",
		"[data-react-aria-pressable]",
		"[data-trigger]",
		// Add React Aria overlay selectors
		...ariaOverlaySelectors,
		// Add DevTools selectors to interactive selector list as well
		...devToolsSelectors,
	].join(", ");

	return element.closest(interactiveSelector) !== null;
};

// Backdrop styling utilities
const backdropColorClasses: Record<BackdropColor, string> = {
	overlay: "bg-overlay",
	black: "bg-black",
	white: "bg-white",
	brand: "bg-brand-solid",
	gray: "bg-gray-900",
	transparent: "bg-transparent",
};

const backdropBlurClasses: Record<BackdropBlur, string> = {
	none: "",
	sm: "backdrop-blur-sm",
	md: "backdrop-blur-md",
	lg: "backdrop-blur-lg",
	xl: "backdrop-blur-xl",
	"2xl": "backdrop-blur-2xl",
	"3xl": "backdrop-blur-3xl",
};

const backdropPresets: Record<BackdropPreset, string> = {
	/** Figma Overlay 3276:15319 — matches Application/Modals/ModalOverlay Storybook. */
	default: "bg-alpha-black-20",
	light: "bg-white/80 backdrop-blur-sm",
	dark: "bg-black/80 backdrop-blur-sm",
	glass: "bg-white/20 backdrop-blur-md",
	frosted: "bg-overlay/60 backdrop-blur-lg",
	minimal: "bg-overlay/40",
	strong: "bg-overlay/90",
	transparent: "bg-transparent",
};

// Generate backdrop classes from configuration
const generateBackdropClasses = (config?: BackdropConfig): string => {
	if (!config) return backdropPresets.default;

	// If custom className is provided, use it directly
	if (config.className) return config.className;

	// If preset is specified, use it (ignores other props)
	if (config.preset) return backdropPresets[config.preset];

	// Build classes from individual props
	const colorClass = config.color
		? backdropColorClasses[config.color]
		: backdropColorClasses.overlay;
	const opacityClass = config.opacity ? `/${config.opacity}` : "/70";
	const blurClass =
		config.blur && config.blur !== "none"
			? backdropBlurClasses[config.blur]
			: "";

	return cx(`${colorClass}${opacityClass}`, blurClass);
};

// Enhanced interface for configurable overlay behavior
interface ModalOverlayProps
	extends AriaModalOverlayProps,
		RefAttributes<HTMLDivElement> {
	// Overlay behavior configuration
	overlayBehavior?: OverlayBehavior;
	shouldCloseOnInteractOutside?: (element: Element) => boolean;
	overlayClassName?: string;

	// Backdrop styling configuration
	backdrop?: BackdropConfig;
	backdropColor?: BackdropColor;
	backdropOpacity?: BackdropOpacity;
	backdropBlur?: BackdropBlur;
	backdropPreset?: BackdropPreset;

	// Convenience props for common use cases
	allowPageInteraction?: boolean; // Shorthand for "interactive" mode
	persistentOverlay?: boolean; // Shorthand for "persistent" mode
}

// Smart close handler factory
const createCloseHandler = (
	behavior: OverlayBehavior,
	customHandler?: (element: Element) => boolean,
) => {
	switch (behavior) {
		case "persistent":
			return () => false; // Never close on outside click

		case "interactive":
			return (element: Element) => !isInteractiveElement(element);

		case "custom":
			return customHandler || (() => true);
		default:
			return () => true; // Always close on outside click
	}
};

export const ModalOverlay = (props: ModalOverlayProps) => {
	const {
		overlayBehavior,
		shouldCloseOnInteractOutside,
		overlayClassName,
		backdrop,
		backdropColor,
		backdropOpacity,
		backdropBlur,
		backdropPreset,
		allowPageInteraction = false,
		persistentOverlay = false,
		...ariaProps
	} = props;

	// Determine final behavior based on prop priority
	let finalBehavior: OverlayBehavior = overlayBehavior || "dismissable";
	if (persistentOverlay) finalBehavior = "persistent";
	if (allowPageInteraction) finalBehavior = "interactive";

	// Create the appropriate close handler
	const closeHandler = createCloseHandler(
		finalBehavior,
		shouldCloseOnInteractOutside,
	);

	// Determine backdrop configuration with prop priority
	let backdropConfig: BackdropConfig | undefined = backdrop;

	// Individual props override backdrop object
	if (backdropPreset || backdropColor || backdropOpacity || backdropBlur) {
		backdropConfig = {
			preset: backdropPreset,
			color: backdropColor,
			opacity: backdropOpacity,
			blur: backdropBlur,
			...backdrop, // backdrop object props have lower priority
		};
	}

	// Generate backdrop classes
	const backdropClasses = generateBackdropClasses(backdropConfig);

	// For transparent backdrop, we need special handling
	const isTransparentBackdrop = backdropClasses.includes("bg-transparent");

	// For transparent backdrop, render without ANY overlay - just pass children through
	if (isTransparentBackdrop) {
		return <>{props.children as React.ReactNode}</>;
	}

	const isDefaultOverlay =
		!backdropPreset ||
		backdropPreset === "default" ||
		backdropClasses === backdropPresets.default;

	// Design-system Overlay (Storybook Application/Modals/ModalOverlay — Figma 3276:15319).
	// Layout overrides dock the drawer to the right; default scrim is alpha-black-20.
	return (
		<DesignSystemOverlay
			{...ariaProps}
			isDismissable={finalBehavior !== "persistent"}
			shouldCloseOnInteractOutside={closeHandler}
			className={(state) =>
				cx(
					"items-center justify-end overflow-hidden p-0 sm:items-center sm:justify-end sm:p-0 pl-6 md:pl-10 ease-linear",
					!isDefaultOverlay && backdropClasses,
					state.isEntering && "duration-300 animate-in fade-in",
					state.isExiting && "duration-500 animate-out fade-out",
					overlayClassName,
					typeof props.className === "function"
						? props.className(state)
						: props.className,
				)
			}
		>
			{props.children}
		</DesignSystemOverlay>
	);
};
ModalOverlay.displayName = "ModalOverlay";

interface ModalProps
	extends AriaModalOverlayProps,
		RefAttributes<HTMLDivElement> {}

export const Modal = (props: ModalProps) => (
	<AriaModal
		{...props}
		className={(state) =>
			cx(
				"inset-y-0 right-0 h-full w-full max-w-100 shadow-xl transition",
				state.isEntering && "duration-300 animate-in slide-in-from-right",
				state.isExiting && "duration-500 animate-out slide-out-to-right",
				typeof props.className === "function"
					? props.className(state)
					: props.className,
			)
		}
	/>
);
Modal.displayName = "Modal";

interface DialogProps extends AriaDialogProps, RefAttributes<HTMLElement> {}

export const Dialog = (props: DialogProps) => (
	<AriaDialog
		role="dialog"
		aria-label="Drawer"
		{...props}
		className={cx(
			"relative flex size-full flex-col items-start gap-6 overflow-y-auto bg-primary ring-1 ring-secondary_alt outline-hidden rounded-lg",
			props.className,
		)}
	/>
);
Dialog.displayName = "Dialog";

// Width configuration types - Enhanced with dynamic options
type DrawerWidth =
	| "xs"
	| "sm"
	| "md"
	| "lg"
	| "xl"
	| "2xl"
	| "full"
	| "1/3"
	| "1/2"
	| "2/3"
	| "3/4"
	| "40%"
	| "50%"
	| "60%"
	| "70%"
	| "80%"
	| "90%";

const drawerWidths: Record<DrawerWidth, string> = {
	// Fixed sizes (responsive)
	xs: "max-w-72", // 288px - Compact
	sm: "max-w-80", // 320px - Mobile friendly
	md: "max-w-96", // 384px - Balanced default
	lg: "max-w-112", // 448px - Comfortable forms
	xl: "max-w-128", // 512px - Detailed content
	"2xl": "max-w-144", // 576px - Wide content
	full: "max-w-full", // 100vw - Full overlay

	// Fractional widths (responsive)
	"1/3": "max-w-[33.333333%]", // 1/3 of viewport
	"1/2": "max-w-[50%]", // 1/2 of viewport
	"2/3": "max-w-[66.666667%]", // 2/3 of viewport
	"3/4": "max-w-[75%]", // 3/4 of viewport

	// Percentage widths (responsive)
	"40%": "max-w-[40%]", // 40% of viewport
	"50%": "max-w-[50%]", // 50% of viewport
	"60%": "max-w-[60%]", // 60% of viewport
	"70%": "max-w-[70%]", // 70% of viewport
	"80%": "max-w-[80%]", // 80% of viewport
	"90%": "max-w-[90%]", // 90% of viewport
};

// Action configuration for footer buttons
interface DrawerFooterAction {
	label: string;
	onClick: () => void;
	variant?:
		| "primary"
		| "secondary"
		| "tertiary"
		| "primary-destructive"
		| "secondary-destructive"
		| "tertiary-destructive";
	size?: "sm" | "md" | "lg";
	loading?: boolean;
	disabled?: boolean;
	icon?: FC<{ className?: string }> | ReactNode;
}

// Enhanced drawer props with overlay configuration
interface DrawerProps
	extends Omit<AriaModalOverlayProps, "children">,
		RefAttributes<HTMLDivElement> {
	children:
		| ReactNode
		| ((children: AriaModalRenderProps & { close: () => void }) => ReactNode);
	dialogClassName?: string;
	width?: DrawerWidth | string;

	// Overlay behavior configuration
	overlayBehavior?: OverlayBehavior;
	shouldCloseOnInteractOutside?: (element: Element) => boolean;
	overlayClassName?: string;

	// Backdrop styling configuration
	backdrop?: BackdropConfig;
	backdropColor?: BackdropColor;
	backdropOpacity?: BackdropOpacity;
	backdropBlur?: BackdropBlur;
	backdropPreset?: BackdropPreset;

	// Convenience props for common use cases
	allowPageInteraction?: boolean; // Shorthand for "interactive" mode
	persistentOverlay?: boolean; // Shorthand for "persistent" mode
}

const Menu = ({
	children,
	dialogClassName,
	width = "md",
	overlayBehavior,
	shouldCloseOnInteractOutside,
	overlayClassName,
	backdrop,
	backdropColor,
	backdropOpacity,
	backdropBlur,
	backdropPreset,
	allowPageInteraction,
	persistentOverlay,
	isOpen,
	onOpenChange,
	...props
}: DrawerProps) => {
	// Determine width class - support both preset and custom widths
	const widthClass =
		width in drawerWidths ? drawerWidths[width as DrawerWidth] : width;

	// Check if this is a transparent backdrop
	const isTransparentBackdrop =
		backdropPreset === "transparent" ||
		(backdrop && backdrop.preset === "transparent") ||
		backdropColor === "transparent";

	// Always create ref and effect, but only use them for transparent backdrops
	const drawerRef = useRef<HTMLDivElement>(null);

	// Animation state machine for transparent backdrops
	type AnimationState = "closed" | "entering" | "open" | "exiting";
	const [animationState, setAnimationState] =
		useState<AnimationState>("closed");

	// Animation state management for transparent backdrops.
	// IMPORTANT: timers are started in a SEPARATE effect run from the state
	// transition that triggers them.  If we call setAnimationState("exiting") and
	// start a timer in the same run, React's cleanup fires before the timer fires
	// (because setAnimationState causes a re-render → cleanup → new effect run),
	// which clears the timer and leaves animationState stuck at "exiting" forever.
	// By splitting the transition (one run) from the timer (next run) the cleanup
	// that cancels the timer only fires after the timer itself has already fired.
	useEffect(() => {
		if (!isTransparentBackdrop) return;

		// ── Enter: closed → entering (state only, no timer) ──────────────────
		if (isOpen && animationState === "closed") {
			setAnimationState("entering");
			return;
		}

		// ── Enter: entering → open (timer starts here, after state settled) ──
		if (isOpen && animationState === "entering") {
			const enterTimer = setTimeout(() => {
				setAnimationState("open");
			}, 300);
			return () => clearTimeout(enterTimer);
		}

		// ── Exit: open|entering → exiting (state only, no timer) ─────────────
		if (
			!isOpen &&
			(animationState === "open" || animationState === "entering")
		) {
			setAnimationState("exiting");
			return;
		}

		// ── Exit: exiting → closed (timer starts here, after state settled) ──
		if (!isOpen && animationState === "exiting") {
			const exitTimer = setTimeout(() => {
				setAnimationState("closed");
			}, 500);
			return () => clearTimeout(exitTimer);
		}
	}, [isTransparentBackdrop, isOpen, animationState]);

	// Global click handler for smart dismiss behavior (only active for transparent backdrops)
	useEffect(() => {
		if (!isTransparentBackdrop || !isOpen) return;

		const handleDocumentClick = (event: MouseEvent) => {
			const rawTarget = event.target;
			const target =
				rawTarget instanceof Element
					? rawTarget
					: rawTarget instanceof Node
						? rawTarget.parentElement
						: null;
			if (!target) return;

			// console.log("🎯 Drawer handleDocumentClick triggered:", {
			// 	target: target.tagName,
			// 	className: target.className,
			// 	dataRac: target.getAttribute("data-rac"),
			// 	role: target.getAttribute("role"),
			// });

			// Don't close if clicking inside drawer
			if (drawerRef.current?.contains(target)) {
				// console.log("🏠 Click inside drawer - not closing");
				return;
			}

			const eventPath = event.composedPath();

			// Clicks that remount the target (e.g. table header sort via Aria Collection
			// dependencies) leave event.target detached before this delayed check runs.
			// composedPath still includes the drawer node from event time.
			if (
				drawerRef.current &&
				eventPath.some((node) => node === drawerRef.current)
			) {
				return;
			}

			// Detached targets are not true outside clicks (outside clicks hit connected nodes)
			if (target instanceof Node && !target.isConnected) {
				return;
			}

			// SPECIAL CASE: Check if this click is from a React Aria Select or similar component
			// by looking at the event's path (composed path includes all elements in the event chain)
			const hasReactAriaInPath = eventPath.some((pathElement) => {
				if (pathElement instanceof Element) {
					// Check for React Aria attributes
					if (
						pathElement.hasAttribute("data-rac") ||
						pathElement.hasAttribute("data-react-aria-top-layer") ||
						pathElement.hasAttribute("data-overlay-container") ||
						pathElement.hasAttribute("data-popover")
					) {
						return true;
					}

					// Check for ARIA roles that indicate overlays
					const role = pathElement.getAttribute("role");
					if (
						role === "listbox" ||
						role === "menu" ||
						role === "dialog" ||
						role === "option" ||
						role === "row"
					) {
						return true;
					}

					// Check for high z-index positioned elements
					if (pathElement instanceof HTMLElement) {
						const style = window.getComputedStyle(pathElement);
						const zIndex = parseInt(style.zIndex || "0", 10);
						if (
							zIndex > 1000 &&
							(style.position === "fixed" || style.position === "absolute")
						) {
							return true;
						}
					}
				}
				return false;
			});

			if (hasReactAriaInPath) {
				// console.log(
				// 	"✅ Found React Aria component in event path - preventing drawer close",
				// );
				return;
			}

			// Don't close if clicking on interactive element - let it function normally
			const isInteractive = isInteractiveElement(target, drawerRef);
			// console.log("🔍 Interactive element check result:", isInteractive);

			if (isInteractive) {
				// console.log("✅ Interactive element detected - not closing drawer");
				return;
			}

			// Close drawer for non-interactive clicks (empty space, text, etc.)
			// console.log("❌ Non-interactive click - closing drawer");
			onOpenChange?.(false);
		};

		// Use a small delay to allow React Aria to process the click first
		const handleDocumentClickDelayed = (event: MouseEvent) => {
			setTimeout(() => {
				handleDocumentClick(event);
			}, 0);
		};

		// Add listener with bubble phase to allow React Aria to process first
		document.addEventListener("click", handleDocumentClickDelayed, false);

		return () => {
			document.removeEventListener("click", handleDocumentClickDelayed, false);
		};
	}, [isTransparentBackdrop, isOpen, onOpenChange]);

	// For transparent backdrop, render as a simple sidebar without any overlay
	if (isTransparentBackdrop && animationState !== "closed") {
		// Calculate actual pixel width based on viewport for fixed positioning
		const getActualWidth = (
			widthClass: string,
		): { className: string; style?: React.CSSProperties } => {
			// Handle percentage widths - calculate actual pixels
			if (widthClass.includes("%")) {
				const percentage = widthClass.match(/\[(\d+)%\]/)?.[1];
				if (percentage) {
					const pixelWidth =
						(window.innerWidth * parseInt(percentage, 10)) / 100;
					return { className: "", style: { width: `${pixelWidth}px` } };
				}
			}

			// Handle fractional widths - calculate actual pixels
			const fractionMap: Record<string, number> = {
				"max-w-[33.333333%]": 33.333333,
				"max-w-[50%]": 50,
				"max-w-[66.666667%]": 66.666667,
				"max-w-[75%]": 75,
			};

			if (fractionMap[widthClass]) {
				const pixelWidth = (window.innerWidth * fractionMap[widthClass]) / 100;
				return { className: "", style: { width: `${pixelWidth}px` } };
			}

			// Handle fixed pixel widths - use Tailwind classes
			if (widthClass.startsWith("max-w-")) {
				return { className: widthClass.replace("max-w-", "w-") };
			}

			// Return as-is for custom widths
			return { className: widthClass };
		};

		const { className: widthClassName, style: widthStyle } =
			getActualWidth(widthClass);

		return (
			<div
				ref={drawerRef}
				className={cx(
					// Fixed right shell, no overlay; z-50 matches modal overlays and sits above workspace Sidebar (z-40)
					"fixed top-0 right-0 h-screen z-50",
					widthClassName,
					"shadow-xl transition-transform ease-in-out",
					// Animation classes based on state
					animationState === "entering" &&
						"duration-300 animate-in slide-in-from-right",
					animationState === "exiting" &&
						"duration-500 animate-out slide-out-to-right",
					overlayClassName,
					typeof props.className === "string" ? props.className : "",
				)}
				style={widthStyle}
			>
				<section
					className={cx(
						"relative flex size-full flex-col items-start gap-6 overflow-y-auto bg-primary ring-1 ring-secondary_alt outline-hidden rounded-lg",
						dialogClassName,
					)}
					role="dialog"
					aria-label="Drawer"
				>
					{typeof children === "function"
						? children({
								isEntering: animationState === "entering",
								isExiting: animationState === "exiting",
								state: {
									isOpen:
										animationState === "open" || animationState === "entering",
									setOpen: (open: boolean) => onOpenChange?.(open),
									open: () => onOpenChange?.(true),
									close: () => onOpenChange?.(false),
									toggle: () => onOpenChange?.(!isOpen),
								} as {
									isOpen: boolean;
									setOpen: (open: boolean) => void;
									open: () => void;
									close: () => void;
									toggle: () => void;
								},
								close: () => onOpenChange?.(false),
							})
						: children}
				</section>
			</div>
		);
	}

	// For non-transparent backdrops or when closed, use normal React Aria Modal
	return (
		<ModalOverlay
			{...props}
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			overlayBehavior={overlayBehavior}
			shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
			overlayClassName={overlayClassName}
			backdrop={backdrop}
			backdropColor={backdropColor}
			backdropOpacity={backdropOpacity}
			backdropBlur={backdropBlur}
			backdropPreset={backdropPreset}
			allowPageInteraction={allowPageInteraction}
			persistentOverlay={persistentOverlay}
		>
			<Modal
				className={(state) =>
					cx(
						widthClass,
						typeof props.className === "function"
							? props.className(state)
							: props.className,
					)
				}
			>
				{(state) => (
					<Dialog className={dialogClassName}>
						{({ close }) => {
							return typeof children === "function"
								? children({ ...state, close })
								: children;
						}}
					</Dialog>
				)}
			</Modal>
		</ModalOverlay>
	);
};
Menu.displayName = "Drawer";

const Content = ({ role = "main", ...props }: ComponentPropsWithRef<"div">) => {
	return (
		<div
			role={role}
			{...props}
			className={cx(
				"flex size-full flex-col gap-6 overflow-y-auto overscroll-auto px-4 md:px-6",
				props.className,
			)}
		/>
	);
};
Content.displayName = "DrawerContent";

// Visual element size configuration
const visualElementSizes = {
	xs: "size-4", // 16px - Extra Small
	sm: "size-5", // 20px - Small
	md: "size-8", // 32px - Default (was sm)
	lg: "size-10", // 40px - Comfortable (was md)
	xl: "size-12", // 48px - Prominent (was lg)
	"2xl": "size-14", // 56px - Large (was xl)
	"3xl": "size-16", // 64px - Extra Large (was 2xl)
};

// Visual element component that handles icons, images, and fallbacks
interface VisualElementProps {
	// Icon props
	icon?: FC<{ className?: string }> | ReactNode;
	iconTheme?:
		| "light"
		| "gradient"
		| "dark"
		| "modern"
		| "outline"
		| "modern-neue";
	iconColor?: "brand" | "gray" | "error" | "warning" | "success";

	// Image props
	imageSrc?: string;
	imageAlt?: string;
	imageFallback?: FC<{ className?: string }> | ReactNode;

	// Common props
	size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
	className?: string;
}

const VisualElement = ({
	icon,
	iconTheme = "light",
	iconColor = "brand",
	imageSrc,
	imageAlt,
	imageFallback,
	size = "md",
	className,
}: VisualElementProps) => {
	const [imageError, setImageError] = useState(false);

	// If image is provided and hasn't errored, show image
	if (imageSrc && !imageError) {
		return (
			<div
				className={cx(
					"shrink-0 overflow-hidden rounded-lg",
					visualElementSizes[size],
					className,
				)}
			>
				<img
					src={imageSrc}
					alt={imageAlt || ""}
					className="size-full object-cover"
					onError={() => setImageError(true)}
				/>
			</div>
		);
	}

	// If image failed and fallback provided, show fallback
	if (imageError && imageFallback) {
		if (isReactComponent(imageFallback)) {
			const FallbackComponent = imageFallback as FC<{ className?: string }>;
			return (
				<div
					className={cx(
						"shrink-0 flex items-center justify-center rounded-lg bg-tertiary",
						visualElementSizes[size],
						className,
					)}
				>
					<FallbackComponent
						className={cx(
							size === "xs"
								? "size-3"
								: size === "sm"
									? "size-4"
									: size === "md"
										? "size-5"
										: size === "lg"
											? "size-6"
											: size === "xl"
												? "size-7"
												: size === "2xl"
													? "size-8"
													: "size-10",
						)}
					/>
				</div>
			);
		}
		return (
			<div
				className={cx(
					"shrink-0 flex items-center justify-center rounded-lg bg-tertiary",
					visualElementSizes[size],
					className,
				)}
			>
				{typeof imageFallback === "function" ? null : imageFallback}
			</div>
		);
	}

	// If icon provided, show FeaturedIcon
	if (icon) {
		return (
			<div className={cx("shrink-0", className)}>
				<FeaturedIcon
					icon={icon}
					theme={iconTheme}
					color={iconColor}
					size={
						size === "xs"
							? "sm"
							: // Map xs to sm for FeaturedIcon
								size === "3xl" || size === "2xl"
								? "xl"
								: size
					} // FeaturedIcon doesn't support xs, map to sm
				/>
			</div>
		);
	}

	// Default fallback - document icon
	return (
		<div
			className={cx(
				"shrink-0 flex items-center justify-center rounded-lg bg-tertiary",
				visualElementSizes[size],
				className,
			)}
		>
			<File01
				className={cx(
					"text-fg-secondary",
					size === "xs"
						? "size-2"
						: size === "sm"
							? "size-3"
							: size === "md"
								? "size-5"
								: size === "lg"
									? "size-6"
									: size === "xl"
										? "size-7"
										: size === "2xl"
											? "size-8"
											: "size-10",
				)}
			/>
		</div>
	);
};

// Enhanced header props with structured content and image support
interface DrawerHeaderProps
	extends Omit<ComponentPropsWithRef<"header">, "title"> {
	// Layout variant
	variant?: "default" | "document" | "prominent" | "image";

	// Icon configuration
	icon?: FC<{ className?: string }> | ReactNode;
	iconTheme?:
		| "light"
		| "gradient"
		| "dark"
		| "modern"
		| "outline"
		| "modern-neue";
	iconColor?: "brand" | "gray" | "error" | "warning" | "success";
	iconSize?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
	showIcon?: boolean; // Control visibility of icon/avatar

	// Image configuration
	imageSrc?: string;
	imageAlt?: string;
	imageSize?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
	imageFallback?: FC<{ className?: string }> | ReactNode;

	// Content
	title?: string | ReactNode;
	description?: string;
	children?: ReactNode; // Fallback for custom content

	// Layout
	showDivider?: boolean;
	showCloseButton?: boolean;

	// Close button customization
	closeButtonClassName?: string;

	// Events
	onClose?: () => void;

	// Job drawer specific props
	isJobOrTicketDrawer?: boolean;
	StatusColor?: "blue-light" | "blue" | "purple" | "success" | "gray" | "error";
	StatusName?: string;
	StatusIcon?: IconComponentType;
	TypeColor?: "blue-light" | "blue" | "purple" | "success" | "gray" | "error";
	TypeName?: string;
	TypeIcon?: IconComponentType;
}

const Header = ({
	className,
	children,
	onClose,
	variant = "default",
	icon,
	iconTheme = "light",
	iconColor = "brand",
	iconSize,
	showIcon = true,
	imageSrc,
	imageAlt,
	imageSize = "lg",
	imageFallback,
	title,
	description,
	showDivider = true,
	showCloseButton = true,
	closeButtonClassName,
	isJobOrTicketDrawer = false,
	StatusColor,
	StatusName,
	StatusIcon,
	TypeColor,
	TypeName,
	TypeIcon,
	...props
}: DrawerHeaderProps) => {
	// If children provided, use legacy behavior
	if (children) {
		return (
			<header
				{...props}
				className={cx("relative z-1 w-full px-4 pt-6 md:px-6", className)}
			>
				<div className="flex items-start gap-4">
					<div className="flex-1 min-w-0">{children}</div>
					{showCloseButton && (
						<CloseButton
							size="lg"
							className={cx(closeButtonClassName || "shrink-0 self-center")}
							onClick={onClose}
						/>
					)}
				</div>
				{showDivider && (
					<div className="w-full h-px bg-border-secondary mt-6" />
				)}
			</header>
		);
	}

	// Determine visual element size based on variant
	const getVisualSize = () => {
		if (imageSrc) return imageSize;
		// For document variant, use iconSize if explicitly provided, otherwise default to 2xl
		if (variant === "document") return iconSize ?? "2xl";
		if (variant === "prominent") return iconSize ?? "xl";
		return iconSize ?? "sm"; // Default iconSize when not provided
	};

	const visualSize = getVisualSize();
	const hasVisualElement =
		showIcon && (icon || imageSrc || variant === "document");

	// Layout classes based on variant
	const getLayoutClasses = () => {
		switch (variant) {
			case "document":
				return "flex items-start gap-4";
			case "prominent":
				return "flex items-center gap-4";
			case "image":
				return "flex items-start gap-4";
			default:
				return "flex items-start gap-4";
		}
	};

	// Content classes based on variant
	const getContentClasses = () => {
		switch (variant) {
			case "document":
				return "flex-1 min-w-0";
			case "prominent":
				return "flex-1 min-w-0 text-center";
			case "image":
				return "flex-1 min-w-0";
			default:
				return "flex-1 min-w-0";
		}
	};

	// Title classes based on variant
	const getTitleClasses = () => {
		switch (variant) {
			case "document":
				return "text-xl font-semibold text-fg-primary truncate";
			case "prominent":
				return "text-2xl font-bold text-fg-primary truncate";
			case "image":
				return "text-lg font-semibold text-fg-primary truncate";
			default:
				return "text-lg font-semibold text-fg-primary truncate";
		}
	};

	// Enhanced structured header
	return (
		<header
			{...props}
			className={cx("relative z-1 w-full px-4 pt-6 md:px-6", className)}
		>
			<div className={getLayoutClasses()}>
				{/* Visual Element */}
				{hasVisualElement && (
					<VisualElement
						icon={icon}
						iconTheme={iconTheme}
						iconColor={iconColor}
						imageSrc={imageSrc}
						imageAlt={imageAlt}
						imageFallback={imageFallback}
						size={visualSize}
						className={variant === "prominent" ? "mx-auto" : "mt-1"}
					/>
				)}

				{/* Content */}
				<div className={getContentClasses()}>
					{title && (
						<h2 className={getTitleClasses()}>
							{typeof title === "string" ? title : title}
						</h2>
					)}
					<div className="flex items-center gap-2 mt-1">
						{isJobOrTicketDrawer && StatusName && StatusColor && StatusIcon && (
							<BadgeWithIcon
								size="sm"
								color={StatusColor}
								children={StatusName}
								iconLeading={StatusIcon}
								className="gap-2"
							/>
						)}
						{isJobOrTicketDrawer && TypeName && TypeColor && TypeIcon && (
							<BadgeWithIcon
								size="sm"
								color={TypeColor}
								children={TypeName}
								iconLeading={TypeIcon}
								className="gap-2"
							/>
						)}
					</div>
					{description && (
						<p
							className={cx(
								"text-sm text-fg-secondary mt-1",
								variant === "prominent" && "text-center",
							)}
						>
							{description}
						</p>
					)}
				</div>

				{/* Close Button */}
				{showCloseButton && (
					<CloseButton
						size="md"
						className={cx(
							closeButtonClassName || "ml-auto shrink-0 self-center",
						)}
						onClick={onClose}
					/>
				)}
			</div>

			{/* Divider */}
			{showDivider && <div className="w-full h-px bg-border-secondary mt-6" />}
		</header>
	);
};
Header.displayName = "DrawerHeader";

// Enhanced footer props with action configuration
interface DrawerFooterProps extends ComponentPropsWithRef<"footer"> {
	// Quick configuration
	primaryAction?: DrawerFooterAction;
	secondaryAction?: DrawerFooterAction;

	// Advanced configuration
	actions?: DrawerFooterAction[];
	layout?: "left" | "right" | "between" | "center" | "stretch";

	// Custom content fallback
	children?: ReactNode;

	// Styling
	showDivider?: boolean;
}

const Footer = ({
	primaryAction,
	secondaryAction,
	actions,
	layout = "right",
	children,
	showDivider = true,
	className,
	...props
}: DrawerFooterProps) => {
	// Layout classes for button arrangements
	const layoutClasses = {
		left: "justify-start",
		right: "justify-end",
		between: "justify-between",
		center: "justify-center",
		stretch: "justify-stretch [&>*]:flex-1",
	};

	// If children provided, use legacy behavior
	if (children) {
		return (
			<footer
				{...props}
				className={cx(
					"w-full p-4 md:px-6",
					showDivider &&
						"shadow-[inset_0px_1px_0px_0px] shadow-border-secondary",
					className,
				)}
			>
				{children}
			</footer>
		);
	}

	// Build actions array from props
	const allActions: DrawerFooterAction[] = [];

	if (actions) {
		allActions.push(...actions);
	} else {
		if (secondaryAction) allActions.push(secondaryAction);
		if (primaryAction) allActions.push(primaryAction);
	}

	// Don't render if no actions
	if (allActions.length === 0) {
		return null;
	}

	return (
		<footer
			{...props}
			className={cx(
				"w-full p-4 md:px-6",
				showDivider && "shadow-[inset_0px_1px_0px_0px] shadow-border-secondary",
				className,
			)}
		>
			<div className={cx("flex items-center gap-3", layoutClasses[layout])}>
				{allActions.map((action, index) => {
					// Determine button variant based on position and explicit variant
					let variant = action.variant;
					if (!variant) {
						// Auto-assign variants: last button is primary, others secondary
						variant = index === allActions.length - 1 ? "primary" : "secondary";
					}

					return (
						<Button
							key={`${action.label}-${index}`}
							color={variant}
							size={action.size || "md"}
							isLoading={action.loading}
							isDisabled={action.disabled}
							onClick={action.onClick}
							iconLeading={action.icon}
						>
							{action.label}
						</Button>
					);
				})}
			</div>
		</footer>
	);
};
Footer.displayName = "DrawerFooter";

const Drawer = Menu as typeof Menu & {
	Trigger: typeof AriaDialogTrigger;
	Content: typeof Content;
	Header: typeof Header;
	Footer: typeof Footer;
	ScrollBody: typeof ScrollBody;
};
Drawer.displayName = "Drawer";

Drawer.Trigger = AriaDialogTrigger;
Drawer.Content = Content;
Drawer.Header = Header;
Drawer.Footer = Footer;
Drawer.ScrollBody = ScrollBody;

export { Drawer };
export type { DrawerScrollBodyProps } from "./ScrollBody";
export { ScrollBody } from "./ScrollBody";
